import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { Alert } from 'ui-components/src/Alert/Alert';
import { Button } from 'ui-components/src/Button';
import { Carousel, CarouselPage } from 'ui-components/src/Carousel';
import { FormField } from 'ui-components/src/FormField';
import { Text } from 'ui-components/src/Text';
import { yup } from 'ui-components/src/yup';
import { v1 } from '../../../endpoints/src/v1';
import styles from './styles.module.scss';

interface ICheckGiftCodeForm {
  gift_code: string;
}

interface IRedeemGiftCodeForm {
  email_address: string;
}

const CheckGiftCodeFormField = FormField<ICheckGiftCodeForm>();
const RedeemGiftCodeFormField = FormField<IRedeemGiftCodeForm>();

export default () => {
  const [page, setPage] = useState<'enter-code' | 'redeem' | 'existing-account' | 'success'>('enter-code');

  const [notFoundError, setNotFoundError] = useState(false);
  const [alreadyRedeemedError, setAlreadyRedeemedError] = useState(false);
  const [genericError, setGenericError] = useState<string>();
  const [otherError, setOtherError] = useState(false);

  const [subscribedThrough, setSubscribedThrough] = useState<string>();

  const checkGiftCodeForm = useFormik<ICheckGiftCodeForm>({
    initialValues: {
      gift_code: '',
    },
    validationSchema: yup.object().shape({
      gift_code: yup.string().required('Please enter a gift code')
    }),
    onSubmit: async values => {
      setNotFoundError(false);
      setOtherError(false);
      setAlreadyRedeemedError(false);

      try {
        const res = await fetch(`${v1.gifts}?gift_code=${values.gift_code}`);

        switch (res.status) {
          case 200:
            setPage('redeem');
            return;
          case 404:
            setNotFoundError(true);
            return;
          case 422:
            setAlreadyRedeemedError(true);
            return;
          default:
            throw new Error();
        }
      } catch (e) {
        setOtherError(true);
      }
    }
  });

  const redeemGiftCodeForm = useFormik<IRedeemGiftCodeForm>({
    initialValues: {
      email_address: ''
    },
    validationSchema: yup.object().shape({
      email_address: yup.string().required('Please enter an email address').email('Please enter a valid email address')
    }),
    onSubmit: async values => {
      setNotFoundError(false);
      setGenericError(undefined);
      setOtherError(false);

      try {
        const res = await fetch(v1.redeemGift, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...values,
            gift_code: checkGiftCodeForm.values.gift_code
          })
        });

        if (res.status === 201) {
          setSubscribedThrough((await res.text()).replace(/"/g, ''));

          setPage('success');
        } else if (res.status === 404) {
          setNotFoundError(true);
        } else {
          const body = await res.json();

          setGenericError(body.message);
        }
      } catch (e) {
        setOtherError(true);
      }
    }
  });

  return <div className={styles.container}>
    {page === 'enter-code' && <div>
      <FormikProvider value={checkGiftCodeForm}>
        <Form className={styles.vspacing}>
          <Text.Paragraph>Enter your gift code below to get started.</Text.Paragraph>
          <CheckGiftCodeFormField
            fieldName="gift_code"
          />
          {notFoundError && <Alert type="error">We couldn't find this gift code.</Alert>}
          {alreadyRedeemedError && <Alert type="error">This gift code has already been redeemed.</Alert>}
          {otherError && <Alert type="error">An unknown error occurred. Please try again later.</Alert>}
          <Button
            type="submit"
            indicateProgress={checkGiftCodeForm.isSubmitting}
          >Verify</Button>
        </Form>
      </FormikProvider>
    </div>}
    {page === 'redeem' && <div className={styles.vspacing}>
      <Text.H2>This code is valid!</Text.H2>
      <Text.Paragraph>How would you like to use it?</Text.Paragraph>
      <Button
        onClick={() => setPage('existing-account')}
      >
        Add to an existing Kodable account
      </Button>
      <Button
        onClick={() => window.location.href = `${process.env.PUBLIC_WEBSITE_URL}/register?type=parent&gift_code=${checkGiftCodeForm.values.gift_code}`}
      >
        Create a new Kodable account
      </Button>
    </div>}
    {page === 'existing-account' && <div className={styles.vspacing}>
      <FormikProvider value={redeemGiftCodeForm}>
        <Form className={styles.vspacing}>
          <Text.H2>This code is valid!</Text.H2>
          <Text.Paragraph>Please enter the email address for the Kodable account you wish to use</Text.Paragraph>

          <RedeemGiftCodeFormField
            fieldName="email_address"
          />

          {notFoundError && <Alert type="error">We couldn't find an account for this email address.</Alert>}
          {!!genericError && <Alert type="error">{genericError}</Alert>}
          {otherError && <Alert type="error">An unknown error occurred. Please try again later.</Alert>}

          <Button
            type="submit"
            indicateProgress={redeemGiftCodeForm.isSubmitting}
          >Redeem</Button>
          <Text.Small>This action is not reversible</Text.Small>
        </Form>
      </FormikProvider>
    </div>}
    {page === 'success' && <div className={styles.vspacing}>
      <Text.H2>Success!</Text.H2>
      <Text.Paragraph>This gift code has been redeemed.</Text.Paragraph>
      <Text.Paragraph>The account {redeemGiftCodeForm.values.email_address} is now subscribed through {subscribedThrough}.</Text.Paragraph>
      <Button
        onClick={() => window.location.href = `${process.env.PUBLIC_WEBSITE_URL}/login?type=parent`}
      >Log in with upgraded account</Button>
    </div>
    }
  </div>;
}